import { type ReactNode, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, useForm } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmailField } from "@/components/ui/email-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { emailSchema } from "@/lib/validation";

const WFP_EMAIL_DOMAIN = "@wfp.org";

export const MANAGER_FORM_ID = "manager-form";

interface CountryOption {
  value: number | string;
  label: string;
}

const countryOptionSchema = z.object({
  value: z.union([z.string(), z.number()]),
  label: z.string(),
});

const createManagerSchema = (
  requiredMessage: string,
  invalidEmailMessage: string,
  wfpEmailMessage: string
) =>
  z.object({
    first_name: z.string().trim().min(1, { message: requiredMessage }),
    last_name: z.string().trim().min(1, { message: requiredMessage }),
    email: emailSchema({ required: requiredMessage, invalid: invalidEmailMessage }).refine(
      value => value.toLowerCase().endsWith(WFP_EMAIL_DOMAIN),
      { message: wfpEmailMessage }
    ),
    countries: z.array(countryOptionSchema).min(1, { message: requiredMessage }),
  });

export type ManagerFormValues = z.infer<ReturnType<typeof createManagerSchema>>;

interface Country {
  id: number | string;
  name: string;
}

interface ManagerFormProps {
  countries: Country[];
  onValidate: (values: ManagerFormValues) => void;
  fieldErrors?: { data?: Record<string, unknown> };
  initialValues?: Partial<ManagerFormValues>;
}

interface FieldShellProps {
  id: string;
  label: string;
  children: ReactNode;
  errorMessage?: string;
  required?: boolean;
  className?: string;
}

const toErrorMessage = (error: unknown): string => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (Array.isArray(error)) {
    return error.join(", ");
  }

  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return String(error);
};

const FieldShell = ({
  id,
  label,
  children,
  errorMessage,
  required = false,
  className,
}: FieldShellProps) => (
  <div className={cn("space-y-2", className)}>
    <Label htmlFor={id} className="font-semibold text-foreground">
      {label}
      {required && (
        <span aria-hidden="true" className="ml-0.5 text-destructive">
          *
        </span>
      )}
    </Label>
    {children}
    {errorMessage && (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    )}
  </div>
);

interface CountriesMultiSelectProps {
  id: string;
  options: CountryOption[];
  value: CountryOption[];
  placeholder: string;
  hasError?: boolean;
  onChange: (value: CountryOption[]) => void;
}

const CountriesMultiSelect = ({
  id,
  options,
  value,
  placeholder,
  hasError = false,
  onChange,
}: CountriesMultiSelectProps) => {
  const selectedValues = value ?? [];
  const isSelected = (option: CountryOption) =>
    selectedValues.some(selected => selected.value === option.value);

  const toggleOption = (option: CountryOption) => {
    if (isSelected(option)) {
      onChange(selectedValues.filter(selected => selected.value !== option.value));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <WFPButton
          id={id}
          type="button"
          variant="outline"
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          className="h-auto min-h-9 w-full justify-between whitespace-normal py-1.5 text-left font-normal"
        >
          {selectedValues.length ? (
            <span className="flex min-w-0 flex-wrap gap-1">
              {selectedValues.map(option => (
                <Badge key={option.value} variant="secondary">
                  {option.label}
                </Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
        </WFPButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-72 w-(--radix-dropdown-menu-trigger-width) overflow-auto"
      >
        {options.map(option => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={isSelected(option)}
            onCheckedChange={() => toggleOption(option)}
            onSelect={event => event.preventDefault()}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ManagerForm = ({
  countries,
  onValidate,
  fieldErrors = {},
  initialValues,
}: ManagerFormProps) => {
  const { t } = useTranslation();

  const countryOptions = useMemo<CountryOption[]>(
    () => countries.map(({ id, name }) => ({ value: id, label: name })),
    [countries]
  );

  const hasCountryChoice = countryOptions.length !== 1;

  const schema = useMemo(
    () =>
      createManagerSchema(
        t("traderDetails.form.mandatoryFieldBlankError"),
        t("traderDetails.form.invalidEmailError"),
        t("managerDetails.wfpEmailOnly")
      ),
    [t]
  );

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ManagerFormValues>({
    defaultValues: {
      first_name: initialValues?.first_name ?? "",
      last_name: initialValues?.last_name ?? "",
      email: initialValues?.email ?? "",
      countries: initialValues?.countries?.length
        ? initialValues.countries
        : hasCountryChoice
          ? []
          : countryOptions,
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });

  const serverErrors = fieldErrors.data;

  useEffect(() => {
    clearErrors();

    if (!serverErrors || Object.keys(serverErrors).length === 0) {
      return;
    }

    Object.entries(serverErrors).forEach(([fieldName, message]) => {
      setError(fieldName as FieldPath<ManagerFormValues>, {
        type: "server",
        message: toErrorMessage(message),
      });
    });
  }, [clearErrors, serverErrors, setError]);

  const getFieldError = (fieldName: keyof ManagerFormValues) =>
    toErrorMessage(errors[fieldName]?.message);

  return (
    // min-w-0: the dialog is a grid, so without it a wide field stretches the whole panel
    <form
      id={MANAGER_FORM_ID}
      noValidate
      className="min-w-0 space-y-5"
      onSubmit={handleSubmit(onValidate)}
    >
      <Controller
        control={control}
        name="first_name"
        render={({ field }) => {
          const error = getFieldError("first_name");

          return (
            <FieldShell
              id="first_name"
              label={t("managerDetails.firstName")}
              errorMessage={error}
              required
            >
              <Input
                {...field}
                id="first_name"
                aria-invalid={!!error}
                aria-describedby={error ? "first_name-error" : undefined}
                className="border-border"
              />
            </FieldShell>
          );
        }}
      />

      <Controller
        control={control}
        name="last_name"
        render={({ field }) => {
          const error = getFieldError("last_name");

          return (
            <FieldShell
              id="last_name"
              label={t("managerDetails.lastName")}
              errorMessage={error}
              required
            >
              <Input
                {...field}
                id="last_name"
                aria-invalid={!!error}
                aria-describedby={error ? "last_name-error" : undefined}
                className="border-border"
              />
            </FieldShell>
          );
        }}
      />

      <EmailField control={control} name="email" label={t("managerDetails.email")} required />

      {hasCountryChoice && (
        <Controller
          control={control}
          name="countries"
          render={({ field }) => {
            const error = getFieldError("countries");

            return (
              <FieldShell
                id="countries"
                label={t("managerDetails.countriesLabel")}
                errorMessage={error}
                required
              >
                <CountriesMultiSelect
                  id="countries"
                  options={countryOptions}
                  value={field.value ?? []}
                  placeholder={t("managerDetails.countriesPlaceholder")}
                  hasError={!!error}
                  onChange={field.onChange}
                />
              </FieldShell>
            );
          }}
        />
      )}
    </form>
  );
};
